import React from 'react'
import getConfig from 'next/config'
import NextRouter from 'next/router'
import { CmsPage, RenderCmsComponent } from 'bloomreach-experience-react-sdk'
import { Organization, WebPage } from '@grandvision/analytics'
import {
  DefaultPageProps,
  getAlgoliaConfigFromBloomreach,
  getPageSeo,
} from '@grandvision/bloomreach'
import {
  AlgoliaConfigProvider,
  AlgoliaConfigProviderProps,
  fetchAlgoliaConfig,
} from '@grandvision/search'
import { Locales } from '@grandvision/state'

import { createAlgoliaConfig } from '../../util/algolia'
import componentDefinitions from '../../cms/component-definitions'
import Layout from '../../components/Layout'

const { publicRuntimeConfig } = getConfig()

/** Safely parse labels as JSON */
const parseJsonLabel = (label: string): { [key: string]: string } => {
  if (!label) {
    return {}
  }

  let jsonLabel = {}

  try {
    jsonLabel = JSON.parse(label)
  } catch (error) {
    console.warn('Error parsing json label: ' + error)
  }

  return jsonLabel
}

interface Props extends DefaultPageProps {
  algoliaConfig: AlgoliaConfigProviderProps
}

const ContainerPage = ({
  pageModel,
  asPath,
  preview,
  request,
  labels,
  isSolaris,
  algoliaConfig,
}: Props) => {
  if (!pageModel) {
    console.error(`No pageModel found for path: ${asPath}!`)
    return null
  }

  const seoData = getPageSeo(pageModel)

  return (
    <CmsPage
      cmsUrls={publicRuntimeConfig.bloomreach.cmsUrls}
      pageModel={{ ...pageModel, labels, request }}
      request={request}
      componentDefinitions={componentDefinitions}
    >
      {() => {
        const content = (
          <Layout pageModel={pageModel} asPath={asPath} isSolaris={isSolaris} preview={preview}>
            <RenderCmsComponent />
            {pageModel.page.name === 'homepage' && (
              <>
                <WebPage
                  pageType="Home"
                  url={seoData.canonicalUrl}
                  name={seoData.title}
                  description={seoData.description}
                />
                {!!labels && !!labels.structureddata && !!labels.structureddata.organization && (
                  <Organization {...parseJsonLabel(labels.structureddata.organization)} />
                )}
              </>
            )}
          </Layout>
        )

        if (!algoliaConfig.isEnabled) {
          return content
        }

        return (
          <AlgoliaConfigProvider {...algoliaConfig} NextRouter={NextRouter}>
            {content}
          </AlgoliaConfigProvider>
        )
      }}
    </CmsPage>
  )
}

ContainerPage.getInitialProps = async ({ asPath, query }) => {
  const { pageModel, request, isSolaris, preview } = query as any

  const algoliaConfig = await fetchAlgoliaConfig({
    ...createAlgoliaConfig(asPath),
    ...getAlgoliaConfigFromBloomreach(pageModel),
    locale: Locales[query.locale],
  })

  return {
    asPath,
    pageModel,
    preview,
    request,
    isSolaris,
    algoliaConfig,
  }
}

export default ContainerPage
